import { PointerEvent, useEffect, useRef } from 'react';
import { Button, Space, Typography } from 'antd';

const Signature = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
    const canvas = useRef<HTMLCanvasElement>(null);
    const pointer = useRef<number | null>(null);

    useEffect(() => {
        const surface = canvas.current;
        const context = surface?.getContext('2d');
        if (!surface || !context) return undefined;
        context.clearRect(0, 0, surface.width, surface.height);
        if (!value) return undefined;
        const image = new Image();
        image.onload = () => context.drawImage(image, 0, 0);
        image.src = value;
        return () => { image.onload = null; };
    }, [value]);

    const draw = (event: PointerEvent<HTMLCanvasElement>, start: boolean) => {
        const surface = event.currentTarget;
        const context = surface.getContext('2d');
        if (!context) return;
        const bounds = surface.getBoundingClientRect();
        const x = (event.clientX - bounds.left) * surface.width / bounds.width;
        const y = (event.clientY - bounds.top) * surface.height / bounds.height;
        if (start) {
            pointer.current = event.pointerId;
            surface.setPointerCapture(event.pointerId);
            context.beginPath();
            context.moveTo(x, y);
            context.lineTo(x + 0.1, y);
        } else {
            if (pointer.current !== event.pointerId) return;
            context.lineTo(x, y);
        }
        context.lineWidth = 2;
        context.lineCap = 'round';
        context.stroke();
    };

    const finish = (event: PointerEvent<HTMLCanvasElement>) => {
        if (pointer.current !== event.pointerId) return;
        pointer.current = null;
        onChange(event.currentTarget.toDataURL('image/png'));
    };

    return (
        <Space direction='vertical' style={{ width: '100%' }}>
            <Typography.Text type='secondary'>Draw your signature with your mouse, stylus or finger.</Typography.Text>
            <canvas
                ref={canvas}
                width={760}
                height={160}
                aria-label='Draw applicant signature'
                style={{ width: '100%', maxWidth: 760, touchAction: 'none', border: '1px solid #d9d9d9', borderRadius: 6 }}
                onPointerDown={(event) => draw(event, true)}
                onPointerMove={(event) => draw(event, false)}
                onPointerUp={finish}
                onPointerCancel={finish}
            />
            <Button onClick={() => onChange('')} disabled={!value}>Clear signature</Button>
        </Space>
    );
};

export default Signature;
